import { createFileRoute } from "@tanstack/react-router"
import { max, min } from "lodash"
import { useMemo } from "react"
import { WorkspacePage } from "~/components/design-system/pages"
import CollectionInfoCard from "~/components/demand-plan/CollectionInfoCard"
import { SalesAndForecastGraphCard } from "~/components/home/SalesAndForecastGraphCard"
import NpiList from "~/components/npi/NpiList"
import { NPI_PRESETS } from "~/components/npi/npiPresets"
import ViewConfiguration from "~/components/planning/ViewConfiguration"
import { NOOS_NEW_PRODUCTS_TABLE_ID } from "~/constants"
import { useCollectionContext } from "~/context/CollectionProvider"
import { useFilters } from "~/hooks/useFilters"
import { usePageTracking } from "~/hooks/usePageTracking"
import { useTableSearch } from "~/hooks/useTableSearch"
import { useVariantContext } from "~/context/VariantProvider"
import { useSkuStore } from "~/providers/sku-store-provider"
import { filterVariants } from "~/utils/filter"

export const Route = createFileRoute(
  "/_auth/demand-planning/noos/$collectionId/_layout/new-variants",
)({
  component: RouteComponent,
})

function RouteComponent() {
  usePageTracking("NOOS - New Products")

  const { variants: rawVariants } = useVariantContext()
  // Phased-out variants are dropped by forecast_base_data.sql, so their NPI columns can only ever
  // come back empty — listing them here offers actions that cannot land. lifecycleStatus is the
  // shared effective-lifecycle value (custom > ERP), the same definition that SQL filters on.
  const npiVariants = useMemo(
    () => rawVariants.filter((v) => v.isNpi && v.settings.lifecycleStatus !== "phasedOut"),
    [rawVariants],
  )
  const { filters, filteredCollections } = useFilters()
  const { effectiveSearchText } = useTableSearch(NOOS_NEW_PRODUCTS_TABLE_ID)
  const { collections, collectionGroups } = useCollectionContext()
  const selectedWarehouseId = useSkuStore((state) => state.warehouseId)
  const selectedChannel = useSkuStore((state) => state.channel)

  const collectionGroup = useMemo(() => {
    const groupId = collections[0]?.collectionGroupId
    return groupId != null ? collectionGroups?.find((g) => g.id === groupId) : undefined
  }, [collections, collectionGroups])

  // The grid applies the search itself; handing it a search-filtered list rebuilt its columns on
  // every keystroke. The search narrows only the graph.
  const gridVariants = useMemo(() => filterVariants(filters, npiVariants), [npiVariants, filters])
  const variantIds = useMemo(
    () => filterVariants([], gridVariants, effectiveSearchText).map((v) => v.variantId),
    [gridVariants, effectiveSearchText],
  )

  const collectionStartDate = useMemo(
    () => min(filteredCollections.map((c) => c.startDate)),
    [filteredCollections],
  )
  const collectionEndDate = useMemo(
    () => max(filteredCollections.map((c) => c.endDate)),
    [filteredCollections],
  )

  return (
    <WorkspacePage
      fillContent
      inspectorWidth="lg"
      viewConfiguration={
        <ViewConfiguration tableId={NOOS_NEW_PRODUCTS_TABLE_ID} presetViews={NPI_PRESETS} />
      }
      inspector={
        <>
          <CollectionInfoCard
            collections={collections}
            collectionGroup={collectionGroup}
            variants={rawVariants}
          />
          <SalesAndForecastGraphCard
            variantIds={variantIds}
            warehouseId={selectedWarehouseId}
            channel={selectedChannel}
            startDate={collectionStartDate ?? undefined}
            endDate={collectionEndDate ?? undefined}
            isNoos
            expandable
          />
        </>
      }
    >
      <NpiList id={NOOS_NEW_PRODUCTS_TABLE_ID} npiVariants={gridVariants} />
    </WorkspacePage>
  )
}
